import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTheme } from "../lib/theme";
import { buildDoorPanels, DoorHingeCue, DoorPlacementPreview, HandleGlyph, SvgTrashButton, ElementActionCluster } from "./cabinet/DoorTechnical";
import { resolveDoorStandard } from "../lib/configurator/doorRules";

function CabinetSideProfile({ height, depth, sections, carcassColor, options, svgId }) {
  const { theme } = useTheme();
  const isDarkUI = theme === "dark";
  const txtCol = isDarkUI ? "#E2E8F0" : "#0F172A";
  const badgeBg = isDarkUI ? "#1D2129" : "#FFFFFF";
  const fondCol = isDarkUI ? "#38BDF8" : "#075985";
  const noFondCol = isDarkUI ? "#F87171" : "#991B1B";
  const scale = Math.min(2.05, 430 / Math.max(height, depth));
  const w = depth * scale;
  const h = height * scale;
  const ox = 76;
  const oy = 34;
  const plinthMm = options?.plinth ? 100 : 0;
  const plinthPx = (plinthMm / 10) * scale;
  const drawerYs = sections.flatMap((section) => (section.elements || []).filter((element) => element.kind === "drawer").map((element) => element.y_mm));
  const shelfYs = sections.flatMap((section) => (section.elements || []).filter((element) => element.kind === "shelf_fixed" || element.kind === "shelf_mobile").map((element) => ({ y: element.y_mm, kind: element.kind })));
  const penderieYs = sections.flatMap((section) => (section.elements || []).filter((element) => element.kind === "penderie").map((element) => element.y_mm));
  return (
    <div data-testid="cabinet-side-visualizer" className="w-full h-full flex items-center justify-center relative">
      <svg id={svgId} width="100%" height="100%" viewBox={`0 0 ${w + 152} ${h + 112}`} preserveAspectRatio="xMidYMid meet" style={{ maxHeight: "100%", maxWidth: "100%" }}>
        {options?.under_slope_rear_enabled ? <polygon points={`${ox},${oy + h} ${ox + w},${oy + h} ${ox + w},${oy + h * (1 - (options.under_slope_rear_low_height_mm || 1200) / (height * 10))} ${ox},${oy}`} fill={carcassColor} opacity="0.82" stroke={txtCol} strokeWidth="2" /> : <rect x={ox} y={oy} width={w} height={h} fill={carcassColor} opacity="0.82" stroke={txtCol} strokeWidth="2" />}
        {options?.has_back_panel !== false ? <><line x1={ox + 7} y1={oy + 4} x2={ox + 7} y2={oy + h - 4} stroke="#0284C7" strokeWidth="3" /><text x={ox + 15} y={oy + 16} fontSize="8" fill={fondCol}>Fond</text></> : <><line x1={ox + 7} y1={oy + 4} x2={ox + 7} y2={oy + h - 4} stroke="#DC2626" strokeWidth="1.5" strokeDasharray="4 3" /><text x={ox + 15} y={oy + 16} fontSize="8" fill={noFondCol}>Sans fond</text></>}
        {drawerYs.map((y, index) => {
          const py = oy + h * (1 - y / (height * 10));
          return <g key={`side-drawer-${index}`}><rect x={ox + 13} y={py - 8} width={Math.min(w - 26, 130)} height="16" fill="#405166" stroke={txtCol} strokeWidth="1.2" rx="1" /><line x1={ox + 13} y1={py} x2={ox + Math.min(w - 13, 143)} y2={py} stroke="#FFF" strokeWidth="1" /><text x={ox + Math.min(w - 13, 143) + 5} y={py + 3} fontSize="8" fill={txtCol}>Tiroir</text></g>;
        })}
        {shelfYs.map((shelf, index) => {
          const py = oy + h * (1 - shelf.y / (height * 10));
          return <g key={`side-shelf-${index}`}><line x1={ox + 12} y1={py} x2={ox + w - 12} y2={py} stroke="#8A5A2B" strokeWidth="5" /><text x={ox + w - 10} y={py - 5} textAnchor="end" fontSize="8" fill={txtCol}>{shelf.kind === "shelf_fixed" ? "Tablette fixe" : "Tablette mobile"}</text></g>;
        })}
        {penderieYs.map((y, index) => {
          const py = oy + h * (1 - y / (height * 10));
          // Vue de côté : la barre de penderie est vue en coupe → un simple point.
          return <g key={`side-penderie-${index}`}><circle cx={ox + w / 2} cy={py} r="4" fill="#475569" stroke={txtCol} strokeWidth="1" /><text x={ox + w / 2 + 8} y={py + 3} fontSize="8" fill={txtCol}>Penderie</text></g>;
        })}
        {options?.plinth && <><rect x={ox} y={oy + h - plinthPx} width={w} height={plinthPx} fill="#B7BCC5" stroke={txtCol} strokeWidth="1" /><text x={ox + w / 2} y={oy + h - 5} textAnchor="middle" fontSize="8" fill={txtCol}>Plinthe {plinthMm} mm</text></>}
        {!options?.plinth && options?.feet_visible !== false && <><line x1={ox + 12} y1={oy + h} x2={ox + 12} y2={oy + h + 15} stroke={txtCol} strokeWidth="3" /><line x1={ox + w - 12} y1={oy + h} x2={ox + w - 12} y2={oy + h + 15} stroke={txtCol} strokeWidth="3" /></>}
        <line x1={ox} y1={oy + h + 42} x2={ox + w} y2={oy + h + 42} stroke="#0284C7" strokeWidth="1.5" />
        <path d={`M${ox} ${oy + h + 42} l7 -4 v8 z M${ox + w} ${oy + h + 42} l-7 -4 v8 z`} fill="#0284C7" />
        <rect x={ox + w / 2 - 42} y={oy + h + 32} width="84" height="19" rx="3" fill={badgeBg} stroke="#0284C7" />
        <text x={ox + w / 2} y={oy + h + 46} textAnchor="middle" fontSize="11" fontWeight="700" fill={txtCol}>P {Math.round(depth * 10)} mm</text>
        <line x1={ox - 30} y1={oy} x2={ox - 30} y2={oy + h} stroke="#0284C7" strokeWidth="1.5" />
        <path d={`M${ox - 30} ${oy} l-4 7 h8 z M${ox - 30} ${oy + h} l-4 -7 h8 z`} fill="#0284C7" />
        <rect x={ox - 62} y={oy + h / 2 - 14} width="60" height="28" rx="3" fill={badgeBg} stroke="#0284C7" />
        <text x={ox - 32} y={oy + h / 2 - 1} textAnchor="middle" fontSize="10" fontWeight="700" fill={txtCol}>H {Math.round(height * 10)}</text>
        <text data-testid="side-height-feet-note" x={ox - 32} y={oy + h / 2 + 10} textAnchor="middle" fontSize="6.5" fill={txtCol}>sans pieds (+100 si pieds)</text>
        <text x={ox + w / 2} y={oy - 12} textAnchor="middle" fontSize="11" fontWeight="700" fill={txtCol}>Vue de côté</text>
      </svg>
    </div>
  );
}

export default function CabinetVisualizer({
  totalWidth = 120,
  height = 200,
  depth = 45,
  sections = [],
  carcassColor = "#B8935A",
  doorsColor = null,
  doorsDifferent = false,
  showDoors = true,
  handles = [],
  onSectionClick = null,
  onPlaceDoor = null,
  onFacadeMode = null,
  onFacadePart = null,
  onResizeDoorPart = null,
  activeMode = null,
  onDropElement = null,
  onMoveElement = null,   // (sectionIdx, elId, y_mm)
  onResizeElement = null, // (sectionIdx, elId, edge, targetEdgeYMm) — façade de tiroir redimensionnable
  onEditSectionWidth = null, // (sectionIdx, widthMm) — saisie manuelle de la largeur d'une section
  onRemoveElement = null, // (sectionIdx, elId)
  onDuplicateElement = null, // (sectionIdx, elId) — copie un élément juste à côté
  onElementSelect = null, // ({ sectionIdx, elId })
  onRemoveDoor = null,    // (sectionIdx) — supprime toute la porte de la section
  onRemoveDoorSegment = null, // (sectionIdx, topMm, heightMm) — supprime une zone de porte
  onOpenDoorMenu = null, // (sectionIdx, clientX, clientY) — ouvre le menu d'options d'ouverture
  onRemoveSection = null,
  onSectionSelect = null,
  onExtendDrawer = null,  // (sectionIdx, elId) — étend d'une travée à droite
  onReduceDrawer = null,  // (sectionIdx, elId) — réduit d'une travée jusqu'au tiroir simple
  onSeparatorDrag = null, // (leftIdx, deltaCm) — redistribution largeurs adjacentes
  onResizeTotal = null,   // (axis: "width"|"height", deltaCm, startValueCm) — redimensionne le meuble entier
  svgId = "cabinet-svg",
  separations = 0,
  options = null,               // Lot 15 : { has_back_panel, plinth, fileur_left/right/top, feet_visible }
  templateVariant = "battant",  // "open" | "battant" | "coulissant" | "sdb" | "sdb_column"
  sdbDrawers = 0,               // Nombre de tiroirs façade pour un meuble SdB (1/2/3), sinon 0
  twoDView = "front",
  defaultHandleId = null,       // Poignée unifiée choisie (dropdown "Poignée") — fallback quand sec.handle_id n'est pas défini
}) {
  const { theme } = useTheme();
  const mutedCol = theme === "dark" ? "#9BA3B0" : "#5F6570";
  const [hoverInfo, setHoverInfo] = useState(null);
  const doorResizeRef = useRef(null);
  useEffect(() => {
    const move = (event) => {
      const drag = doorResizeRef.current;
      if (!drag || !onResizeDoorPart) return;
      const mm = Math.max(80, Math.min(drag.maxMm, drag.startMm + (event.clientY - drag.startY) / drag.pxPerMm * (drag.bottom ? -1 : 1)));
      onResizeDoorPart(drag.sectionIdx, drag.partKey, mm);
    };
    const up = () => { doorResizeRef.current = null; };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [onResizeDoorPart]);
  // Chantier 2.2 : redimensionnement de la façade de tiroir (bord haut ou bas), pour
  // couvrir tout le caisson ou se coller parfaitement à un tiroir voisin (gap 0 autorisé).
  const elResizeRef = useRef(null);
  useEffect(() => {
    const move = (event) => {
      const drag = elResizeRef.current;
      if (!drag || !onResizeElement) return;
      const deltaMm = -(event.clientY - drag.startY) / drag.pxPerMm;
      onResizeElement(drag.sectionIdx, drag.elId, drag.edge, drag.startEdgeYMm + deltaMm);
    };
    const up = () => { elResizeRef.current = null; };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [onResizeElement]);
  const [dragging, setDragging] = useState(null);
  const [hoveredEl, setHoveredEl] = useState(null);
  const [editingSectionWidthIdx, setEditingSectionWidthIdx] = useState(null);
  const [sectionWidthError, setSectionWidthError] = useState(null); // { idx, message } — chantier 2.4
  const commitSectionWidthEdit = (idx, raw) => {
    setEditingSectionWidthIdx(null);
    const mm = Math.round(Number(raw));
    if (!Number.isFinite(mm) || mm < 200) {
      setSectionWidthError({ idx, message: "Minimum 200 mm" });
      setTimeout(() => setSectionWidthError((prev) => (prev?.idx === idx ? null : prev)), 2500);
      return;
    }
    setSectionWidthError(null);
    onEditSectionWidth?.(idx, mm);
  };
  const [selectedEl, setSelectedEl] = useState(null);
  const [sepDragging, setSepDragging] = useState(null); // {leftIdx} pour affichage
  const [resizeDragging, setResizeDragging] = useState(null); // {axis} pour affichage
  // Refs pour un drag stable (évite re-mount du useEffect sur chaque state change)
  const sepDragRef = useRef(null);         // { leftIdx, startClientX, startWLeft, startWRight, pxPerCm }
  const onSeparatorDragRef = useRef(onSeparatorDrag);
  useEffect(() => { onSeparatorDragRef.current = onSeparatorDrag; }, [onSeparatorDrag]);
  const resizeDragRef = useRef(null);      // { axis, startClient, startValueCm, pxPerCm }
  const onResizeTotalRef = useRef(onResizeTotal);
  useEffect(() => { onResizeTotalRef.current = onResizeTotal; }, [onResizeTotal]);

  // pointermove/up global pendant drag natif SVG
  const [crossSectionTarget, setCrossSectionTarget] = useState(null); // idx de la section survolée pendant un drag inter-sections
  useEffect(() => {
    if (!dragging) return;
    const heightMmTotal = height * 10;
    const onMove = (e) => {
      // Chantier 3.2 : glisser-déposer un élément vers une AUTRE section — on détecte la
      // zone de clic réellement survolée (par la position X du pointeur), pas seulement
      // la section d'origine, pour permettre de "lâcher" l'élément ailleurs.
      const allZones = Array.from(document.querySelectorAll('[data-testid^="click-zone-"]'));
      let zone = allZones.find((z) => {
        const r = z.getBoundingClientRect();
        return e.clientX >= r.left && e.clientX <= r.right;
      });
      if (!zone) zone = document.querySelector(`[data-testid="click-zone-${String.fromCharCode(65 + dragging.sectionIdx)}"]`);
      if (!zone) return;
      const letter = zone.getAttribute("data-testid").slice(-1);
      const targetSectionIdx = letter.charCodeAt(0) - 65;
      const rect = zone.getBoundingClientRect();
      const yFrac = (e.clientY - rect.top) / rect.height;
      const yMm = Math.round(heightMmTotal * (1 - Math.min(1, Math.max(0, yFrac))));
      setCrossSectionTarget(targetSectionIdx !== dragging.sectionIdx ? targetSectionIdx : null);
      onMoveElement && onMoveElement(dragging.sectionIdx, dragging.elId, yMm, targetSectionIdx);
    };
    const onUp = () => { setDragging(null); setCrossSectionTarget(null); };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [dragging, height, onMoveElement]);

  // Drag séparation : listeners mount UNE fois (ref-based) → aucun re-mount pendant le drag.
  useEffect(() => {
    const onMove = (e) => {
      const s = sepDragRef.current;
      if (!s || !onSeparatorDragRef.current) return;
      const deltaPx = e.clientX - s.startClientX;
      const deltaCm = deltaPx / s.pxPerCm;
      onSeparatorDragRef.current(s.leftIdx, deltaCm, s.startWLeft, s.startWRight);
    };
    const onUp = () => {
      if (sepDragRef.current) {
        sepDragRef.current = null;
        setSepDragging(null);
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, []);

  // Drag redimensionnement global (bord droit = largeur, bord bas = hauteur) : même principe ref-based.
  // Le delta doit utiliser les pixels réellement affichés (le SVG est souvent mis à l'échelle
  // par preserveAspectRatio), pas seulement ses unités internes.
  useEffect(() => {
    const onMove = (e) => {
      const r = resizeDragRef.current;
      if (!r || !onResizeTotalRef.current) return;
      const deltaPx = r.axis === "width" ? e.clientX - r.startClient : e.clientY - r.startClient;
      // Droite = plus large, bas = plus haut : le geste est identique à l'intuition visuelle.
      const deltaCm = deltaPx / r.pxPerCm;
      onResizeTotalRef.current(r.axis, deltaCm, r.startValueCm);
    };
    const onUp = () => {
      if (resizeDragRef.current) {
        resizeDragRef.current = null;
        setResizeDragging(null);
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, []);

  // Désélection au clic hors élément
  useEffect(() => {
    if (!selectedEl) return;
    const onDocClick = (e) => {
      if (!e.target.closest?.("[data-svg-element]")) setSelectedEl(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [selectedEl]);

  // Adaptive scaling — meuble jusqu'à 270 × 270 cm sans clipping
  const maxW = 460;
  const maxH = 560;
  const heightScale = maxH / 270;
  const widthScale = maxW / 270;
  const scale = Math.min(heightScale, widthScale);

  const w = totalWidth * scale;
  const h = height * scale;

  const sectionCount = Math.max(1, sections.length);

  // Largeurs par section : soit explicites (width_cm), soit égalité
  const explicitWidths = sections.map((s) => s.width_cm || 0);
  const useExplicit = explicitWidths.every((wcm) => wcm > 0);
  const widthsCm = useExplicit ? explicitWidths : Array(sectionCount).fill(totalWidth / sectionCount);
  const sumCm = widthsCm.reduce((a, b) => a + b, 0);
  // Facteur de conversion cm → pixel SVG (basé sur la vraie largeur totale)
  const cmToSvg = w / (sumCm || totalWidth);
  const sectionWidthsPx = widthsCm.map((wcm) => wcm * cmToSvg);
  // Offset X (en px SVG) du bord GAUCHE de chaque section
  const sectionOffsetsPx = sectionWidthsPx.reduce((acc, sw) => {
    acc.push((acc[acc.length - 1] ?? 0) + sw);
    return acc;
  }, [0]);

  const isDark = ["#1F1F1F", "#2C4C3B", "#1E3A5F", "#6B4A2E", "#151515"].includes(carcassColor);
  const strokeCol = isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)";
  const shelfStroke = isDark ? "rgba(255,255,255,0.85)" : "rgba(15,15,15,0.85)";
  const drawerBorder = isDark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.75)";
  const doorFill = doorsDifferent && doorsColor ? doorsColor : carcassColor;
  const isDarkDoor = ["#1F1F1F", "#2C4C3B", "#1E3A5F", "#6B4A2E", "#151515"].includes(doorFill);
  // Poignée réellement sélectionnée (dropdown "Poignée") — sec.handle_id permet une
  // future surcharge par section, mais retombe toujours sur le choix unifié global.
  const resolveHandleFor = (sec) => handles.find((hh) => hh.id === (sec?.handle_id || defaultHandleId)) || null;

  // Fileur gauche : calculé AVANT le padding pour que la cote de hauteur reste
  // toujours à gauche du fileur (sinon chevauchement du texte "Fileur G" avec la cote).
  const leftFileurPx = options?.fileur_left ? Math.max(4, (options.fileur_left_width || 1.5) * cmToSvg) : 0;

  // Padding autour du meuble
  const padTop = 26;
  const padBottom = 110;
  const padLeft = 30 + leftFileurPx; // 30 = marge fixe pour la cote de hauteur (seule colonne restante) + largeur du fileur gauche s'il existe
  const padRight = 12;
  const svgWidth = w + padLeft + padRight;
  const svgHeight = h + padTop + padBottom;
  const originX = padLeft;
  const originY = padTop;
  const traversingDrawers = sections.flatMap((section, sectionIdx) => (section.elements || [])
    .filter((element) => element.kind === "drawer" && (element.span_sections || 1) > 1 && sectionIdx + element.span_sections <= sectionCount)
    .map((element) => ({
      ...element,
      sectionIdx,
      x: originX + sectionOffsetsPx[sectionIdx],
      width: sectionWidthsPx.slice(sectionIdx, sectionIdx + element.span_sections).reduce((sum, sectionWidth) => sum + sectionWidth, 0),
      y: originY + 4 + (h - 8) * (1 - element.y_mm / (height * 10)),
    })));
  // Sous-pente : la géométrie 2D reproduit la découpe en biais.
  // Le rendu ne modifie JAMAIS le state des aménagements (pas de suppression d'éléments).
  const underSlope = options?.under_slope_enabled === true;
  const slopeDirection = options?.under_slope_direction === "right" ? "right" : "left";
  const totalHeightMm = height * 10;
  const lowHeightMm = Math.max(100, Math.min(totalHeightMm - 10, options?.under_slope_low_height_mm || Math.round(totalHeightMm * 0.6)));
  const bevelWidthMm = Math.max(100, Math.min(totalWidth * 10, options?.under_slope_bevel_width_mm || Math.round(Math.min(totalWidth * 10, 600))));
  const bevelPx = (bevelWidthMm / 10) * cmToSvg;
  const lowTopY = originY + h * (1 - lowHeightMm / totalHeightMm);
  const slopeClipId = `${svgId}-under-slope-clip`;
  const grainId = `${svgId}-grain`;
  const hatchId = `${svgId}-hatch`;
  const mirrorGradId = `${svgId}-mirror-gradient`;
  const outerSlopePoints = underSlope
    ? slopeDirection === "left"
      ? `${originX},${originY + h} ${originX + w},${originY + h} ${originX + w},${originY} ${originX + bevelPx},${originY} ${originX},${lowTopY}`
      : `${originX},${originY + h} ${originX + w},${originY + h} ${originX + w},${lowTopY} ${originX + w - bevelPx},${originY} ${originX},${originY}`
    : `${originX},${originY + h} ${originX + w},${originY + h} ${originX + w},${originY} ${originX},${originY}`;
  const slopeTopAtX = (x) => {
    if (!underSlope) return originY;
    if (slopeDirection === "left") {
      if (x >= originX + bevelPx) return originY;
      return lowTopY - ((x - originX) / bevelPx) * (lowTopY - originY);
    }
    if (x <= originX + w - bevelPx) return originY;
    return originY + ((x - (originX + w - bevelPx)) / bevelPx) * (lowTopY - originY);
  };

  const isDraggableKind = (k) => ["drawer", "penderie", "shelf_fixed", "shelf_mobile", "mirror"].includes(k);

  if (twoDView === "side") {
    return <CabinetSideProfile height={height} depth={depth} sections={sections} carcassColor={carcassColor} options={options} svgId={svgId} />;
  }

  return (
    <div data-testid="cabinet-visualizer" className="flex h-full w-full min-w-0 flex-1 items-center justify-center relative">
      <motion.svg
        id={svgId}
        key={`${totalWidth}-${height}-${sectionCount}-${showDoors}`}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        width="100%"
        height="100%"
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio="xMidYMid meet"
        className="block h-full w-full min-w-0"
        style={{ maxHeight: "100%", maxWidth: "100%", flex: "1 1 100%" }}
      >
        <defs>
          <pattern id={grainId} width="60" height="120" patternUnits="userSpaceOnUse">
            <path d="M0 20 Q 15 30 30 20 T 60 20" stroke={isDark ? "rgba(255,255,255,0.08)" : "rgba(80,50,20,0.15)"} fill="none" strokeWidth="1" />
            <path d="M0 60 Q 20 70 40 60 T 60 60" stroke={isDark ? "rgba(255,255,255,0.06)" : "rgba(80,50,20,0.10)"} fill="none" strokeWidth="1" />
          </pattern>
          <linearGradient id={mirrorGradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#C7D2DA" />
            <stop offset="45%" stopColor="#EDF2F5" />
            <stop offset="55%" stopColor="#DDE6EA" />
            <stop offset="100%" stopColor="#AAB8C2" />
          </linearGradient>
          <clipPath id={slopeClipId}>
            <polygon points={outerSlopePoints} />
          </clipPath>
          {/* Hachures diagonales pour le mode "sans fond" */}
          <pattern id={hatchId} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="8" stroke={isDark ? "rgba(255,255,255,0.13)" : "rgba(160,160,170,0.28)"} strokeWidth="1" />
          </pattern>
        </defs>

        {/* Ombre douce sous le meuble */}
        {(() => {
          let shadowOffset = 8;
          if ((templateVariant === "sdb" || templateVariant === "sdb_column") && options?.sdb_foot_ellipse_height) {
            shadowOffset = (options.sdb_foot_ellipse_height / 10) * scale + 4;
          }
          return <ellipse cx={originX + w / 2} cy={originY + h + shadowOffset} rx={w * 0.42} ry={5} fill="rgba(30,36,51,0.22)" />;
        })()}

        {/* Badge coulissant : préparation caisson (portes non fournies) */}
        {templateVariant === "coulissant" && (
          <g pointerEvents="none">
            <rect x={originX + w / 2 - 90} y={originY - 22} width={180} height={16} rx={8} fill="#0465C1" />
            <text x={originX + w / 2} y={originY - 10} textAnchor="middle" fontSize="9" fontWeight="700" fill="#FFF" fontFamily="Outfit">
              Préparé coulissant · +100 mm profondeur
            </text>
          </g>
        )}

        {/* Caisson — vue 2D FRONTALE, pas de perspective 3D */}
        <polygon points={outerSlopePoints} fill={options?.has_back_panel === false ? "#FFFFFF" : carcassColor} />
        {options?.has_back_panel !== false && (
          <polygon points={outerSlopePoints} fill={`url(#${grainId})`} opacity={0.22} />
        )}
        {options?.has_back_panel === false && (
          <>
            <polygon points={outerSlopePoints} fill={`url(#${hatchId})`} />
            <text x={originX + w / 2} y={originY + h / 2 - 4} textAnchor="middle" fontSize="13" fontWeight="700" fill={isDark ? "#A0A0AA" : "#888"} fontStyle="italic" fontFamily="Outfit" opacity={0.7}>SANS FOND</text>
          </>
        )}
        {/* Cadre externe marqué */}
        <polygon points={outerSlopePoints} fill="none" stroke={strokeCol} strokeWidth={1.5} strokeLinejoin="round" />
        {underSlope && (
          <text data-testid="under-slope-2d-label" x={originX + w / 2} y={originY - 10} textAnchor="middle" fontSize="8" fontWeight="700" fill="#0465C1" fontFamily="Outfit">
            Sous-pente · {slopeDirection === "left" ? "gauche" : "droite"} · H basse {lowHeightMm} mm
          </text>
        )}

        {/* Salle de Bain : rendu du dessus selon sdb_top_finish (dessus / vasque) */}
        {options?.sdb_top_finish === "avec_dessus" && templateVariant === "sdb" && (
          <g pointerEvents="none">
            <rect
              x={originX - 4}
              y={originY - 8}
              width={w + 8}
              height={8}
              fill={carcassColor}
              stroke={strokeCol}
              strokeWidth={1}
              rx={1}
            />
            <text x={originX + w / 2} y={originY - 2} textAnchor="middle" fontSize="7" fill="#FFF" fontFamily="Outfit">Plateau finition</text>
          </g>
        )}
        {/* Crédence SdB (jusqu'à 3 côtés : fond, gauche, droite) + plan vasque/porte-serviette —
            toujours blanche, basée sur les côtés actifs des 2 types de crédence (H100/soudée). */}
        {(() => {
          const credenceSides = Array.from(new Set([
            ...((options?.sdb_credence_sides && options.sdb_credence_sides.length) ? options.sdb_credence_sides : ((options?.sdb_credence_ml || 0) > 0 ? ["fond"] : [])),
            ...((options?.sdb_credence_soudee_sides && options.sdb_credence_soudee_sides.length) ? options.sdb_credence_soudee_sides : ((options?.sdb_credence_soudee_ml || 0) > 0 ? ["fond"] : [])),
          ]));
          const hasCredenceFond = templateVariant === "sdb" && credenceSides.includes("fond");
          const hasCredenceGauche = templateVariant === "sdb" && credenceSides.includes("gauche");
          const hasCredenceDroite = templateVariant === "sdb" && credenceSides.includes("droite");
          if (templateVariant !== "sdb") return null;
          const credenceH = 9;
          const isAvecVasque = options?.sdb_top_finish === "avec_vasque";
          const planTopY = isAvecVasque ? (originY - 10) : (originY - (options?.sdb_top_finish === "avec_dessus" ? 8 : 0));
          const sideCredenceH = credenceH + (isAvecVasque ? 10 : 0);
          const sideCredence = (hasCredenceGauche || hasCredenceDroite) && (
            <g pointerEvents="none">
              {hasCredenceGauche && <rect data-testid="sdb-credence-gauche-visual" x={originX - 11} y={planTopY - credenceH} width={7} height={sideCredenceH} fill="#F8F8F5" stroke="#8E9297" strokeWidth={1} rx={1} />}
              {hasCredenceDroite && <rect data-testid="sdb-credence-droite-visual" x={originX + w + 4} y={planTopY - credenceH} width={7} height={sideCredenceH} fill="#F8F8F5" stroke="#8E9297" strokeWidth={1} rx={1} />}
            </g>
          );

          if (!isAvecVasque) {
            return (
              <>
                {hasCredenceFond && (
                  <g data-testid="sdb-credence-visual" pointerEvents="none">
                    <rect x={originX - 4} y={planTopY - credenceH} width={w + 8} height={credenceH} fill="#F8F8F5" stroke="#8E9297" strokeWidth={1} rx={1} />
                    <text x={originX + w / 2} y={planTopY - 2.5} textAnchor="middle" fontSize="6.5" fill="#8E9297" fontFamily="Outfit">Crédence</text>
                  </g>
                )}
                {sideCredence}
              </>
            );
          }

          // Porte-serviette : trou allongé le long de la PROFONDEUR, avec ~5cm de marge
          // avant/arrière — percé dans le DÉBORD du plan (le plan continue au-delà du caisson
          // sur ce côté, comme sur la photo produit : zone "flottante" sans caisson dessous).
          const hasTowelSlot = (options?.sdb_porte_serviette_qty || 0) > 0;
          const towelSide = options?.sdb_porte_serviette_side || "droite";
          const towelSidesActive = hasTowelSlot ? (towelSide === "both" ? ["gauche", "droite"] : [towelSide]) : [];
          const overhangPx = hasTowelSlot ? Math.max(20, w * 0.09) : 0;
          const extendLeft = towelSidesActive.includes("gauche") ? overhangPx : 0;
          const extendRight = towelSidesActive.includes("droite") ? overhangPx : 0;

          // --- Plan "Avec vasque" : plateau résine (débord standard + débord porte-serviette) + vasque(s) ---
          const planX = originX - 4 - extendLeft;
          const planW = w + 8 + extendLeft + extendRight;
          const doubleVasque = !!options?.sdb_double_vasque;
          const vasquePos = options?.sdb_vasque_position || "centre";
          const singleRx = Math.min(w * 0.35, 65);
          const doubleRx = Math.min(w * 0.18, 40);
          const vasqueRx = doubleVasque ? doubleRx : singleRx;
          const vasqueCenters = doubleVasque
            ? [originX + w * 0.27, originX + w * 0.73]
            : [vasquePos === "gauche" ? originX + vasqueRx + 10 : vasquePos === "droite" ? originX + w - vasqueRx - 10 : originX + w / 2];
          return (
            <g pointerEvents="none">
              {hasCredenceFond && (
                <g data-testid="sdb-credence-visual">
                  <rect x={planX} y={originY - 10 - credenceH} width={planW} height={credenceH} fill="#F8F8F5" stroke="#8E9297" strokeWidth={1} rx={1} />
                  <text x={planX + planW / 2} y={originY - 10 - 2.5} textAnchor="middle" fontSize="6.5" fill="#8E9297" fontFamily="Outfit">Crédence</text>
                </g>
              )}
              {(hasCredenceGauche || hasCredenceDroite) && (
                <g>
                  {hasCredenceGauche && <rect data-testid="sdb-credence-gauche-visual" x={planX - 7} y={originY - 10 - credenceH} width={7} height={credenceH + 10} fill="#F8F8F5" stroke="#8E9297" strokeWidth={1} rx={1} />}
                  {hasCredenceDroite && <rect data-testid="sdb-credence-droite-visual" x={planX + planW} y={originY - 10 - credenceH} width={7} height={credenceH + 10} fill="#F8F8F5" stroke="#8E9297" strokeWidth={1} rx={1} />}
                </g>
              )}
              {/* Plateau support de la vasque — toujours en résine (moulage intégré), blanc comme la vasque.
                  Déborde du côté du porte-serviette : le plan "continue" au-delà du caisson. */}
              <rect x={planX} y={originY - 10} width={planW} height={10} fill="#F8F8F5" stroke="#8E9297" strokeWidth={1} rx={1} />
              {/* Vasque(s) blanche(s) (ellipse) — 1 ou 2 si double vasque (≥1200 mm) */}
              {vasqueCenters.map((vasqueCx, i) => (
                <g key={i} data-testid={doubleVasque ? `sdb-vasque-visual-${i}` : "sdb-vasque-visual"}>
                  <ellipse cx={vasqueCx} cy={originY - 5} rx={vasqueRx} ry={5} fill="#F8F8F5" stroke="#8E9297" strokeWidth={1} />
                  <ellipse cx={vasqueCx} cy={originY - 5} rx={Math.max(vasqueRx - 10, 8)} ry={3.5} fill="none" stroke="#D0D3D6" strokeWidth={0.8} />
                  <line x1={vasqueCx} y1={originY - 14} x2={vasqueCx} y2={originY - 6} stroke="#8E9297" strokeWidth={1.2} strokeLinecap="round" />
                  <circle cx={vasqueCx} cy={originY - 15} r={1.5} fill="#8E9297" />
                </g>
              ))}
              {towelSidesActive.map((side) => {
                const slotCx = side === "gauche" ? planX + (overhangPx / 2) + 2 : planX + planW - (overhangPx / 2) - 2;
                return (
                  <rect key={side} data-testid={`sdb-porte-serviette-visual-${side}`} x={slotCx - 2.8} y={originY - 9} width={5.6} height={8} rx={2.8} fill="#2A2A28" stroke="#111" strokeWidth={0.6} />
                );
              })}
            </g>
          );
        })()}

        {/* Click zones / Drop targets par section */}
        {(activeMode || onSectionClick || onDropElement) && sections.map((_s, idx) => {
          const zx = originX + sectionOffsetsPx[idx];
          const zw = sectionWidthsPx[idx];
          // Retour visuel immédiat pendant le glisser/pointage : une zone qui refusera le
          // dépôt (porte déjà posée, découpe de section trop fine, pose sous la sous-pente)
          // s'affiche en rouge plutôt qu'en bleu, pour ne jamais laisser croire que le dépôt
          // sera accepté.
          const isZoneInvalid = (mode, yMm, xFrac) => {
            if (mode === "door") return !!_s.has_door;
            if (mode === "section") {
              const currentWidthCm = _s.width_cm || totalWidth / Math.max(1, sections.length);
              const leftCm = currentWidthCm * xFrac;
              const rightCm = currentWidthCm - leftCm;
              return leftCm < 30 || rightCm < 30;
            }
            if (["drawer", "penderie", "shelf_fixed", "shelf_mobile", "mirror"].includes(mode)) {
              if (!underSlope || yMm <= lowHeightMm) return false;
              const leftCm = sections.slice(0, idx).reduce((sum, section) => sum + (section.width_cm || 0), 0);
              const widthCm = _s.width_cm || 0;
              const bevelCm = bevelWidthMm / 10;
              return slopeDirection === "right" ? leftCm + widthCm > totalWidth - bevelCm : leftCm < bevelCm;
            }
            return false;
          };
          const hovered = hoverInfo?.idx === idx;
          const zoneInvalid = hovered && hoverInfo?.invalid;
          return (
            <rect
              key={`clickzone-${idx}`}
              data-testid={`click-zone-${String.fromCharCode(65 + idx)}`}
              x={zx}
              y={originY}
              width={zw}
              height={h}
              fill={zoneInvalid ? "rgba(220,38,38,0.14)" : activeMode ? "rgba(4,101,193,0.06)" : "transparent"}
              stroke={zoneInvalid ? "rgba(220,38,38,0.85)" : activeMode || hovered ? "rgba(4,101,193,0.5)" : "transparent"}
              strokeWidth={1.5}
              strokeDasharray={activeMode === "door" ? undefined : "6 4"}
              style={{ cursor: zoneInvalid ? "not-allowed" : activeMode ? "crosshair" : "default" }}
              onClick={(e) => {
                if (!activeMode || !onSectionClick) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const yFrac = (e.clientY - rect.top) / rect.height;
                const xFrac = (e.clientX - rect.left) / rect.width;
                onSectionClick(idx, Math.min(1, Math.max(0, yFrac)), Math.min(1, Math.max(0, xFrac)));
              }}
              onMouseMove={(e) => {
                if (!activeMode) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const yFrac = (e.clientY - rect.top) / rect.height;
                const xFrac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                const yMm = Math.round(height * 10 * (1 - yFrac));
                const clampedYMm = Math.max(0, Math.min(height * 10, yMm));
                setHoverInfo(activeMode === "door"
                  ? { idx, kind: "door", invalid: isZoneInvalid(activeMode, clampedYMm, xFrac) }
                  : { idx, kind: activeMode === "section" ? "section" : "position", y_mm: clampedYMm, x_frac: xFrac, invalid: isZoneInvalid(activeMode, clampedYMm, xFrac) });
              }}
              onMouseLeave={() => setHoverInfo(null)}
              onDragOver={(e) => {
                if (!onDropElement) return;
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                const yFrac = (e.clientY - rect.top) / rect.height;
                const xFrac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                const yMm = Math.round(height * 10 * (1 - yFrac));
                const clampedYMm = Math.max(0, Math.min(height * 10, yMm));
                const invalid = isZoneInvalid(activeMode, clampedYMm, xFrac);
                e.dataTransfer.dropEffect = invalid ? "none" : "copy";
                // Une porte couvre toute la travée : elle ne possède pas de cote de hauteur.
                // Le guide horizontal est réservé aux tiroirs, tablettes et penderies.
                setHoverInfo(activeMode === "door"
                  ? { idx, kind: "door", invalid }
                  : { idx, kind: activeMode === "section" ? "section" : "position", y_mm: clampedYMm, x_frac: xFrac, invalid });
              }}
              onDragLeave={() => setHoverInfo(null)}
              onDrop={(e) => {
                if (!onDropElement) return;
                e.preventDefault();
                // Certains navigateurs vident dataTransfer au lâcher sur un SVG.
                // activeMode est défini dès le départ du drag : il garantit que la porte
                // aperçue devient bien une porte persistante au drop.
                const kind = e.dataTransfer.getData("application/x-atelier-element") || activeMode;
                if (!kind) { setHoverInfo(null); return; }
                const rect = e.currentTarget.getBoundingClientRect();
                const yFrac = (e.clientY - rect.top) / rect.height;
                const xFrac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
                const yMm = Math.round(height * 10 * (1 - yFrac));
                onDropElement(idx, Math.max(0, Math.min(height * 10, yMm)), kind, xFrac);
                setHoverInfo(null);
              }}
            />
          );
        })}

        {/* Aperçu de façade pendant le glisser : il reprend le dessin technique final
            (vantail translucide, paumelles, V pointillé et poignée), sans cote de hauteur. */}
        {hoverInfo?.kind === "door" && !hoverInfo.invalid && (
          (() => {
            const previewSection = sections[hoverInfo.idx];
            const px = originX + sectionOffsetsPx[hoverInfo.idx] + 2;
            const pw = sectionWidthsPx[hoverInfo.idx] - 4;
            const py = originY + 6;
            const ph = h - 12;
            const standard = resolveDoorStandard(previewSection, pw / scale);
            const masterSide = previewSection?.door_double_master === "right" ? "right" : "left";
            return <DoorPlacementPreview sectionIndex={hoverInfo.idx} panels={buildDoorPanels({ x: px, width: pw, standard, masterSide })} y={py} height={ph} doorFill={doorFill} grainId={grainId} dark={isDarkDoor} />;
          })()
        )}

        {/* Guide de cote, exclusivement pour les éléments dont la hauteur de pose est utile.
            En rouge si la pose à cet endroit sera refusée (sous-pente, etc.). */}
        {hoverInfo?.kind !== "door" && hoverInfo && (
          <g pointerEvents="none">
            <line
              x1={originX + sectionOffsetsPx[hoverInfo.idx] + 3}
              x2={originX + sectionOffsetsPx[hoverInfo.idx] + sectionWidthsPx[hoverInfo.idx] - 3}
              y1={originY + h * (1 - hoverInfo.y_mm / (height * 10))}
              y2={originY + h * (1 - hoverInfo.y_mm / (height * 10))}
              stroke={hoverInfo.invalid ? "#DC2626" : "#0465C1"}
              strokeWidth={3}
              strokeDasharray="6 3"
              opacity={0.85}
            />
            <rect
              x={originX + sectionOffsetsPx[hoverInfo.idx] + 6}
              y={originY + h * (1 - hoverInfo.y_mm / (height * 10)) - 20}
              width={hoverInfo.invalid ? 96 : 72}
              height={18}
              fill={hoverInfo.invalid ? "#DC2626" : "#0465C1"}
              rx={9}
            />
            <text
              x={originX + sectionOffsetsPx[hoverInfo.idx] + (hoverInfo.invalid ? 54 : 42)}
              y={originY + h * (1 - hoverInfo.y_mm / (height * 10)) - 7}
              fontSize="11"
              fill="#FFF"
              fontFamily="Outfit, sans-serif"
              fontWeight="700"
              textAnchor="middle"
              data-testid="drag-cote-label"
            >
              {hoverInfo.invalid ? "Pose impossible" : `H : ${hoverInfo.y_mm} mm`}
            </text>
          </g>
        )}
        {hoverInfo?.kind === "section" && <line data-testid="section-insertion-guide" x1={originX + sectionOffsetsPx[hoverInfo.idx] + sectionWidthsPx[hoverInfo.idx] * hoverInfo.x_frac} x2={originX + sectionOffsetsPx[hoverInfo.idx] + sectionWidthsPx[hoverInfo.idx] * hoverInfo.x_frac} y1={originY} y2={originY + h} stroke={hoverInfo.invalid ? "#DC2626" : "#0465C1"} strokeWidth={2} strokeDasharray="6 4" pointerEvents="none" />}

        {/* Per-section interior contents */}
        {sections.map((sec, idx) => {
          const sx = originX + sectionOffsetsPx[idx];
          const sw = sectionWidthsPx[idx];
          const sy = originY + 4;
          const sh = h - 8;
          const heightMm = height * 10;
          const label = String.fromCharCode(65 + idx);
          const elements = sec.elements || [];
          const hasElements = elements.length > 0;
          const mmToSvgY = (yMm) => sy + sh * (1 - yMm / heightMm);

          // Fallback legacy
          const drawersCount = hasElements ? 0 : (sec.drawers || 0);
          const posFromBottom = sec.drawers_position ?? 0.0;
          const drawerBlockHeight = drawersCount > 0 ? Math.min(sh * 0.6, drawersCount * 26) : 0;
          const drawerYStart = sy + (sh - drawerBlockHeight) * (1 - posFromBottom);
          const drawerBandH = drawersCount > 0 ? drawerBlockHeight / drawersCount : 0;
          const totalLegacyShelves = hasElements ? 0 : ((sec.shelves_fixed || 0) + (sec.shelves_mobile || 0));
          const shelfSpacing = totalLegacyShelves > 0 ? sh / (totalLegacyShelves + 1) : 0;

          const zx = originX + sectionOffsetsPx[idx];
          const zw = sectionWidthsPx[idx];
          // Helper: convertit un événement (clientX/Y) vers les coordonnées SVG viewBox
          const clientToSvgY = (evt) => {
            const svg = evt.currentTarget.ownerSVGElement;
            if (!svg) return null;
            const pt = svg.createSVGPoint();
            pt.x = evt.clientX;
            pt.y = evt.clientY;
            const ctm = svg.getScreenCTM();
            if (!ctm) return null;
            const local = pt.matrixTransform(ctm.inverse());
            const yFrac = (local.y - originY) / h;
            return Math.round(height * 10 * (1 - Math.max(0, Math.min(1, yFrac))));
          };
          return (
            <g
              key={`interior-${label}`}
              clipPath={underSlope ? `url(#${slopeClipId})` : undefined}
              onDragOver={onDropElement ? (e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "copy";
                const yMm = clientToSvgY(e);
                if (yMm != null) setHoverInfo(activeMode === "door" ? { idx, kind: "door" } : { idx, kind: "position", y_mm: yMm });
              } : undefined}
              onDrop={onDropElement ? (e) => {
                e.preventDefault();
                const kind = e.dataTransfer.getData("application/x-atelier-element") || activeMode;
                if (!kind) { setHoverInfo(null); return; }
                const yMm = clientToSvgY(e);
                if (yMm != null) onDropElement(idx, yMm, kind);
                setHoverInfo(null);
              } : undefined}
            >
              {/* Chantier 3.2 : surbrillance de la section cible pendant un glisser-déposer
                  d'élément venant d'une autre section. */}
              {crossSectionTarget === idx && (
                <rect data-testid={`cross-section-drop-highlight-${idx}`} x={sx} y={sy} width={sw} height={sh} fill="#0465C1" opacity="0.16" stroke="#0465C1" strokeWidth="2.4" strokeDasharray="7 5" pointerEvents="none" />
              )}
              {/* Overlay drop-zone : garantit que dragover se déclenche sur toute la section
                  même quand les éléments internes sont peu couvrants (fines lignes de tablettes). */}
              {onDropElement && (
                <rect
                  data-testid={`interior-dropzone-${label}`}
                  x={zx}
                  y={originY}
                  width={zw}
                  height={h}
                  fill="transparent"
                  // En mode pose, la zone haute du plan est l'unique cible : cela évite
                  // qu'un calque intérieur SVG absorbe le tap mobile avant le click-zone.
                  pointerEvents={activeMode ? "none" : "all"}
                  onClick={(event) => {
                    if (!activeMode || !onSectionClick) return;
                    const rect = event.currentTarget.getBoundingClientRect();
                    const yFrac = (event.clientY - rect.top) / rect.height;
                    const xFrac = (event.clientX - rect.left) / rect.width;
                    onSectionClick(idx, Math.min(1, Math.max(0, yFrac)), Math.min(1, Math.max(0, xFrac)));
                  }}
                />
              )}
              {elements.map((el) => {
                if (el.kind === "drawer" && (el.span_sections || 1) > 1) return null;
                const yEl = mmToSvgY(el.y_mm);
                const isHover = hoveredEl?.elId === el.id;
                const isSelected = selectedEl?.elId === el.id;
                const isDraggingMe = dragging && dragging.elId === el.id;
                const draggable = isDraggableKind(el.kind);

                // Handlers communs à tous les éléments draggables
                const commonProps = draggable ? {
                  "data-svg-element": true,
                  "data-testid": `svg-element-${el.id}`,
                  style: { cursor: "grab", touchAction: "none" },
                  onPointerDown: (evt) => {
                    if (!draggable || !onMoveElement) return;
                    evt.preventDefault();
                    evt.stopPropagation();
                    setSelectedEl({ sectionIdx: idx, elId: el.id });
                    onElementSelect?.({ sectionIdx: idx, elId: el.id });
                    setDragging({ sectionIdx: idx, elId: el.id });
                  },
                  onMouseEnter: () => setHoveredEl({ sectionIdx: idx, elId: el.id }),
                  onMouseLeave: () => setHoveredEl(null),
                } : {};

                const showTrash = (isHover || isSelected) && !el.locked && !!onRemoveElement;
                const removeHere = () => {
                  onRemoveElement(idx, el.id);
                  setSelectedEl(null);
                  setHoveredEl(null);
                };
                const duplicateHere = onDuplicateElement ? () => onDuplicateElement(idx, el.id) : null;

                if (el.kind === "shelf_fixed" || el.kind === "shelf_mobile") {
                  const isFixed = el.kind === "shelf_fixed";
                  const structural = el.locked === true;
                  return (
                    <g key={el.id} {...commonProps}>
                      {/* Hitbox large pour hover confortable */}
                      <rect x={sx + 2} y={yEl - 10} width={sw - 4} height={20} fill="transparent" />
                      <line
                        x1={sx + 4}
                        x2={sx + sw - 4}
                        y1={yEl}
                        y2={yEl}
                        stroke={structural ? "#0465C1" : (isFixed ? shelfStroke : (isDark ? "rgba(180,180,180,0.9)" : "rgba(75,85,99,0.85)"))}
                        strokeWidth={isDraggingMe ? 7 : (structural ? 5 : (isFixed ? 4 : 2.5))}
                        strokeDasharray={isFixed ? "" : "5 3"}
                        strokeLinecap="round"
                        opacity={isDraggingMe ? 0.85 : 1}
                      />
                      {isFixed && (
                        <>
                          <line x1={sx + 4} x2={sx + 4} y1={yEl - 4} y2={yEl + 4} stroke={structural ? "#0465C1" : shelfStroke} strokeWidth={structural ? 2.5 : 2} />
                          <line x1={sx + sw - 4} x2={sx + sw - 4} y1={yEl - 4} y2={yEl + 4} stroke={structural ? "#0465C1" : shelfStroke} strokeWidth={structural ? 2.5 : 2} />
                        </>
                      )}
                      {structural && (
                        <g transform={`translate(${sx + sw - 12}, ${yEl - 12})`}>
                          <rect x={-6} y={-4} width={10} height={8} rx={1.5} fill="#0465C1" />
                          <path d="M-3 -4 Q-3 -8 0 -8 Q3 -8 3 -4" stroke="#0465C1" strokeWidth={1.2} fill="none" />
                        </g>
                      )}
                      {(isHover || isSelected || isDraggingMe) && (
                        <>
                          <rect x={sx + sw / 2 - 40} y={yEl - 22} width={80} height={16} rx={8} fill={structural ? "#0465C1" : "#111"} />
                          <text x={sx + sw / 2} y={yEl - 10} textAnchor="middle" fontSize="10" fontWeight="700" fill="#FFF" fontFamily="Outfit">H : {el.y_mm} mm</text>
                        </>
                      )}
                      <ElementActionCluster cx={sx + sw / 2} cy={yEl + 18} onDuplicate={duplicateHere} onRemove={removeHere} visible={showTrash} elId={el.id} />
                    </g>
                  );
                }
                if (el.kind === "penderie") {
                  const validXs = underSlope ? Array.from({ length: Math.max(1, Math.floor(sw - 16)) }, (_, point) => sx + 8 + point).filter((x) => yEl >= slopeTopAtX(x) + 7) : [];
                  const rodStart = underSlope ? validXs[0] : sx + 10;
                  const rodEnd = underSlope ? validXs[validXs.length - 1] : sx + sw - 10;
                  if (underSlope && (!rodStart || !rodEnd || rodEnd - rodStart < 14)) return null;
                  return (
                    <g key={el.id} {...commonProps}>
                      <rect x={rodStart - 6} y={yEl - 10} width={rodEnd - rodStart + 12} height={20} fill="transparent" />
                      <rect x={rodStart - 2} y={yEl - 8} width={5} height={14} fill={shelfStroke} rx={1} />
                      <rect x={rodEnd - 3} y={yEl - 8} width={5} height={14} fill={shelfStroke} rx={1} />
                      <line x1={rodStart} x2={rodEnd} y1={yEl - 1} y2={yEl - 1} stroke={shelfStroke} strokeWidth={5.5} strokeLinecap="round" />
                      <g transform={`translate(${(rodStart + rodEnd) / 2}, ${yEl + 3})`}>
                        <path d="M-9 4 L9 4 L7 10 L-7 10 Z" stroke={shelfStroke} strokeWidth={2} fill="none" strokeLinejoin="round" />
                        <path d="M-7 4 Q0 -1 7 4" stroke={shelfStroke} strokeWidth={2} fill="none" strokeLinecap="round" />
                      </g>
                      {(isHover || isSelected || isDraggingMe) && (
                        <>
                          <rect x={sx + sw / 2 - 40} y={yEl - 22} width={80} height={16} rx={8} fill="#111" />
                          <text x={sx + sw / 2} y={yEl - 10} textAnchor="middle" fontSize="10" fontWeight="700" fill="#FFF" fontFamily="Outfit">H : {el.y_mm} mm</text>
                        </>
                      )}
                      <ElementActionCluster cx={(rodStart + rodEnd) / 2} cy={yEl + 20} onDuplicate={duplicateHere} onRemove={removeHere} visible={showTrash} elId={el.id} />
                    </g>
                  );
                }
                if (el.kind === "mirror") {
                  const pxPerMmM = scale / 10;
                  const mH = Math.max(14, (el.height_mm || 0) * pxPerMmM);
                  const mTop = yEl - mH / 2;
                  const mwMax = sw - 8;
                  const mw = Math.min(mwMax, Math.max(14, (el.width_mm || 0) * pxPerMmM));
                  const mx = sx + (sw - mw) / 2;
                  return (
                    <g key={el.id} {...commonProps}>
                      <rect x={mx} y={mTop} width={mw} height={mH} rx={2} fill="#DCEAF0" fillOpacity={isDraggingMe ? 0.55 : 0.4} stroke="#6E9FB0" strokeWidth={isSelected ? 2.4 : 1.4} />
                      <line x1={mx + mw * 0.2} y1={mTop + 2} x2={mx + mw * 0.2} y2={mTop + mH - 2} stroke="#FFFFFF" strokeWidth={2} opacity={0.55} />
                      <line x1={mx + mw * 0.55} y1={mTop + 2} x2={mx + mw * 0.55} y2={mTop + mH - 2} stroke="#FFFFFF" strokeWidth={1.2} opacity={0.35} />
                      {(isHover || isSelected || isDraggingMe) && (
                        <>
                          <rect x={sx + sw / 2 - 40} y={yEl - 22} width={80} height={16} rx={8} fill="#111" />
                          <text x={sx + sw / 2} y={yEl - 10} textAnchor="middle" fontSize="10" fontWeight="700" fill="#FFF" fontFamily="Outfit">H : {el.y_mm} mm</text>
                        </>
                      )}
                      <ElementActionCluster cx={sx + sw / 2} cy={mTop + mH + 12} onDuplicate={duplicateHere} onRemove={removeHere} visible={showTrash} elId={el.id} />
                    </g>
                  );
                }
                if (el.kind === "drawer") {
                  const pxPerMm = scale / 10;
                  const drH = Math.max(10, (el.height_mm || 150) * pxPerMm);
                  const drY = yEl - drH / 2;
                  const showResize = (isHover || isSelected || isDraggingMe) && !!onResizeElement;
                  const startResize = (edge) => (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    event.currentTarget.setPointerCapture?.(event.pointerId);
                    const ctm = event.currentTarget.ownerSVGElement?.getScreenCTM();
                    const screenPxPerSvgUnit = ctm ? Math.hypot(ctm.c, ctm.d) : 1;
                    const half = (el.height_mm || 150) / 2;
                    elResizeRef.current = {
                      sectionIdx: idx,
                      elId: el.id,
                      edge,
                      startY: event.clientY,
                      startEdgeYMm: edge === "bottom" ? el.y_mm - half : el.y_mm + half,
                      pxPerMm: Math.max(0.001, screenPxPerSvgUnit * pxPerMm),
                    };
                  };
                  return (
                    <g key={el.id} {...commonProps}>
                      <rect x={sx + 4} y={drY + 2} width={sw - 8} height={drH - 3} fill="rgba(0,0,0,0.18)" rx={2} />
                      <rect x={sx + 3} y={drY + 1} width={sw - 6} height={drH - 2} fill={doorFill} opacity={0.95} stroke={drawerBorder} strokeWidth={2.5} rx={2} />
                      {(() => {
                        const dh = resolveHandleFor(sec);
                        return dh ? <HandleGlyph type={dh.svg_type} x={sx + 3} y={drY + 1} w={sw - 6} h={drH - 2} finishColor={dh.swatch} /> : null;
                      })()}
                      {(isHover || isSelected || isDraggingMe) && (
                        <>
                          <rect x={sx + sw / 2 - 40} y={drY - 14} width={80} height={16} rx={8} fill="#111" />
                          <text x={sx + sw / 2} y={drY - 2} textAnchor="middle" fontSize="10" fontWeight="700" fill="#FFF" fontFamily="Outfit">H : {Math.round(el.height_mm || 150)} mm</text>
                        </>
                      )}
                      {showResize && (
                        <g data-testid={`drawer-resize-top-${el.id}`} data-svg-element style={{ cursor: "ns-resize", touchAction: "none", pointerEvents: "all" }} onPointerDown={startResize("top")}>
                          <rect x={sx + sw / 2 - 14} y={drY - 5} width={28} height={10} fill="transparent" />
                          <rect x={sx + sw / 2 - 11} y={drY - 3} width={22} height={4} rx={2} fill="#2563EB" stroke="#FFF" strokeWidth={0.8} />
                        </g>
                      )}
                      {showResize && (
                        <g data-testid={`drawer-resize-bottom-${el.id}`} data-svg-element style={{ cursor: "ns-resize", touchAction: "none", pointerEvents: "all" }} onPointerDown={startResize("bottom")}>
                          <rect x={sx + sw / 2 - 14} y={drY + drH - 5} width={28} height={10} fill="transparent" />
                          <rect x={sx + sw / 2 - 11} y={drY + drH - 3} width={22} height={4} rx={2} fill="#2563EB" stroke="#FFF" strokeWidth={0.8} />
                        </g>
                      )}
                      <ElementActionCluster cx={sx + sw / 2} cy={drY + drH + 16} onDuplicate={duplicateHere} onRemove={removeHere} visible={showTrash} elId={el.id} />
                    </g>
                  );
                }
                return null;
              })}

              {/* Legacy fallback (sections sans elements list) */}
              {!hasElements && Array.from({ length: totalLegacyShelves }).map((_, i) => {
                const isFixed = i < (sec.shelves_fixed || 0);
                const yShelf = sy + shelfSpacing * (i + 1);
                if (drawersCount > 0 && yShelf >= drawerYStart && yShelf <= drawerYStart + drawerBlockHeight) return null;
                return (
                  <g key={`sh-${i}`}>
                    <line
                      x1={sx + 4}
                      x2={sx + sw - 4}
                      y1={yShelf}
                      y2={yShelf}
                      stroke={isFixed ? shelfStroke : (isDark ? "rgba(180,180,180,0.9)" : "rgba(75,85,99,0.85)")}
                      strokeWidth={isFixed ? 4 : 2.5}
                      strokeDasharray={isFixed ? "" : "5 3"}
                      strokeLinecap="round"
                    />
                  </g>
                );
              })}
              {!hasElements && drawersCount > 0 && Array.from({ length: drawersCount }).map((_, i) => {
                const dy = drawerYStart + drawerBandH * i;
                const dh = resolveHandleFor(sec);
                return (
                  <g key={`dr-${i}`}>
                    <rect x={sx + 4} y={dy + 2} width={sw - 8} height={drawerBandH - 3} fill="rgba(0,0,0,0.18)" rx={2} />
                    <rect x={sx + 3} y={dy + 1} width={sw - 6} height={drawerBandH - 2} fill={doorFill} opacity={0.95} stroke={drawerBorder} strokeWidth={2.5} rx={2} />
                    {dh && <HandleGlyph type={dh.svg_type} x={sx + 3} y={dy + 1} w={sw - 6} h={drawerBandH - 2} finishColor={dh.swatch} />}
                  </g>
                );
              })}

              {/* Salle de Bain : N façades tiroirs pleine largeur remplissant tout le meuble.
                  Pas de plinthe/pieds affichés pour SdB (retiré Feb 15 à la demande client). */}
              {templateVariant === "sdb" && sdbDrawers > 0 && (() => {
                const bandH = sh / sdbDrawers;
                const gap = 2; // ligne fine entre tiroirs
                const dh = resolveHandleFor(sec);
                return (
                  <g data-testid={`sdb-drawers-${sdbDrawers}`}>
                    {Array.from({ length: sdbDrawers }).map((_, i) => {
                      const dy = sy + i * bandH;
                      const drH = bandH - gap;
                      return (
                        <g key={`sdb-dr-${i}`}>
                          {/* Ombre portée sous la façade */}
                          <rect
                            x={sx + 6}
                            y={dy + 3}
                            width={sw - 12}
                            height={drH - 2}
                            fill="rgba(0,0,0,0.18)"
                            rx={3}
                          />
                          {/* Façade tiroir */}
                          <rect
                            x={sx + 4}
                            y={dy + 1}
                            width={sw - 8}
                            height={drH - 1}
                            fill={doorFill}
                            opacity={0.98}
                            stroke={drawerBorder}
                            strokeWidth={2.5}
                            rx={3}
                          />
                          {/* Poignée horizontale centrée — vraie poignée sélectionnée (TIP-ON = aucune) */}
                          {dh && <HandleGlyph type={dh.svg_type} x={sx + 4} y={dy + 1} w={sw - 8} h={drH - 1} finishColor={dh.swatch} />}
                        </g>
                      );
                    })}
                  </g>
                );
              })()}
            </g>
          );
        })}

        {/* Séparations verticales — DRAGGABLES pour ajuster largeurs asymétriques */}
        {Array.from({ length: sectionCount - 1 }).map((_, i) => {
          const xSep = originX + sectionOffsetsPx[i + 1];
          const wLeft = widthsCm[i];
          const wRight = widthsCm[i + 1];
          const draggable = !!onSeparatorDrag;
          // Tiroir traversant : couper la séparation au-dessus du tiroir
          const crossingDrawer = traversingDrawers.find((d) => d.sectionIdx <= i && i < d.sectionIdx + (d.span_sections || 2));
          const pxPerMm = scale / 10;
          const sepY2 = crossingDrawer
            ? crossingDrawer.y - Math.max(10, ((crossingDrawer.height_mm || 150) * pxPerMm)) / 2 - 2
            : originY + h - 2;
          return (
            <g key={`sep-${i}`}>
              <line
                x1={xSep}
                x2={xSep}
                y1={slopeTopAtX(xSep) + 2}
                y2={sepY2}
                stroke={strokeCol}
                strokeWidth={3.5}
                opacity={0.9}
              />
              {draggable && (
                <>
                  {/* Hitbox large invisible pour saisir la séparation */}
                  <rect
                    data-testid={`separator-${i}`}
                    data-svg-element
                    x={xSep - 6}
                    y={slopeTopAtX(xSep) + 2}
                    width={12}
                    height={sepY2 - slopeTopAtX(xSep) - 4}
                    fill="transparent"
                    style={{ cursor: "ew-resize", touchAction: "none" }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Capture immédiate du px/cm réel via le SVG rendu (pas dépendant du DOM live)
                      const zoneA = document.querySelector('[data-testid="click-zone-A"]');
                      const rectA = zoneA?.getBoundingClientRect();
                      const pxPerCm = rectA && widthsCm[0] > 0 ? rectA.width / widthsCm[0] : cmToSvg;
                      sepDragRef.current = {
                        leftIdx: i,
                        startClientX: e.clientX,
                        startWLeft: wLeft,
                        startWRight: wRight,
                        pxPerCm,
                      };
                      setSepDragging({ leftIdx: i });
                    }}
                  />
                  {/* Poignée visuelle : petit rectangle bleu au milieu */}
                  <rect
                    x={xSep - 3}
                    y={originY + h / 2 - 12}
                    width={6}
                    height={24}
                    rx={2}
                    fill={sepDragging?.leftIdx === i ? "#0465C1" : "rgba(4,101,193,0.35)"}
                    pointerEvents="none"
                  />
                  <line x1={xSep - 5} x2={xSep - 5} y1={originY + h / 2 - 4} y2={originY + h / 2 + 4} stroke="#FFF" strokeWidth={0.8} pointerEvents="none" />
                  <line x1={xSep + 5} x2={xSep + 5} y1={originY + h / 2 - 4} y2={originY + h / 2 + 4} stroke="#FFF" strokeWidth={0.8} pointerEvents="none" />
                  {sepDragging?.leftIdx === i && (
                    <>
                      <rect x={xSep - 40} y={originY - 22} width={80} height={16} rx={8} fill="#0465C1" />
                      <text x={xSep} y={originY - 10} textAnchor="middle" fontSize="10" fontWeight="700" fill="#FFF" fontFamily="Outfit">
                        {Math.round(wLeft * 10)} · {Math.round(wRight * 10)} mm
                      </text>
                    </>
                  )}
                </>
              )}
            </g>
          );
        })}

        {/* PORTES — auto-coupées autour des zones tiroirs (Lot 15).
            Lot 15 iter3 : coulissant → PAS de porte dessinée (portes non fournies). */}
        {showDoors && templateVariant !== "open" && templateVariant !== "coulissant" && sections.map((sec, idx) => {
          if (!sec.has_door) return null;
          const sx = originX + 2 + sectionOffsetsPx[idx];
          const dw = sectionWidthsPx[idx] - 4;
          const dh = h - 12;
          const handle = resolveHandleFor(sec);
          const doorDark = isDarkDoor;

          // Détecter les zones tiroirs (kind === "drawer") pour découper les portes
          const drawerBands = sections.flatMap((owner, ownerIdx) => (owner.elements || [])
            .filter((element) => element.kind === "drawer" && ownerIdx <= idx && idx < ownerIdx + (element.span_sections || 1)))
            .map((e) => {
              const yPx = originY + h - h * (e.y_mm / (height * 10));
              return { center: yPx, halfH: 13, y_mm: e.y_mm };
            })
            .sort((a, b) => a.center - b.center);

          // Fusion visuelle des tiroirs proches (< 26 SVG px ≈ 25 mm de gap) en un seul bloc
          const merged = [];
          for (const d of drawerBands) {
            const last = merged[merged.length - 1];
            if (last && d.center - (last.top + last.height) < 8) {
              // Étendre le bloc précédent
              last.height = d.center + d.halfH - last.top;
            } else {
              merged.push({ top: d.center - d.halfH, height: d.halfH * 2 });
            }
          }
          // Calcule les segments libres pour les portes
          const topBoundary = originY + 6;
          const bottomBoundary = originY + 6 + dh;
          const doorSegments = [];
          let cursor = topBoundary;
          merged.forEach((m) => {
            if (m.top - cursor > 15) doorSegments.push({ top: cursor, height: m.top - cursor });
            cursor = m.top + m.height;
          });
          if (bottomBoundary - cursor > 15) doorSegments.push({ top: cursor, height: bottomBoundary - cursor });

          // Si pas de tiroirs → porte pleine
          let finalSegments = merged.length === 0
            ? [{ top: topBoundary, height: dh }]
            : doorSegments;

          // Porte partielle : ne conserve que la zone haute ou basse explicitement choisie.
          if (sec.door_coverage && sec.door_coverage !== "full") {
            const chosenMm = Math.max(200, Math.min(height * 10, sec.door_height_mm || Math.round(height * 10 * 0.6)));
            const chosenPx = (chosenMm / (height * 10)) * dh;
            const coverageTop = sec.door_coverage === "top" ? topBoundary : bottomBoundary - chosenPx;
            const coverageBottom = sec.door_coverage === "top" ? topBoundary + chosenPx : bottomBoundary;
            finalSegments = finalSegments.flatMap((segment) => {
              const start = Math.max(segment.top, coverageTop);
              const end = Math.min(segment.top + segment.height, coverageBottom);
              return end - start > 8 ? [{ top: start, height: end - start }] : [];
            });
          }

          if (templateVariant === "sdb_column") {
             finalSegments = [
               { top: topBoundary, height: dh / 2 - 2 },
               { top: topBoundary + dh / 2 + 2, height: dh / 2 - 2 }
             ];
          }

          const isCoulissant = templateVariant === "coulissant";
          const isMirrorDoor = !!sec.door_mirror;
          const doorFillUsed = isMirrorDoor ? `url(#${mirrorGradId})` : doorFill;
          // Chant haut d'une porte : il épouse le profil du toit (pente latérale) avec le même
          // retrait que la façade (6 px). Le point de cassure de la pente est inséré dans le tracé.
          const roofGap = topBoundary - originY;
          const kinkX = slopeDirection === "left" ? originX + bevelPx : originX + w - bevelPx;
          const leafTopAt = (x, top) => Math.max(top, slopeTopAtX(x) + roofGap);
          const leafPath = (px, pw, top, bottom, inset = 0) => {
            const left = px + inset, right = px + pw - inset;
            const xs = [left, right];
            if (underSlope && kinkX > left && kinkX < right) xs.splice(1, 0, kinkX);
            const topPts = xs.slice().reverse().map((x) => `L ${x} ${leafTopAt(x, top) + inset}`).join(" ");
            return `M ${left} ${bottom - inset} L ${right} ${bottom - inset} ${topPts} Z`;
          };
          return (
            <g key={`door-${idx}`}>
              {finalSegments.map((seg, si) => {
                const dy = seg.top;
                const sh = seg.height;
                const partKey = finalSegments.length === 1 ? "full" : (si === 0 ? "top" : si === finalSegments.length - 1 ? "bottom" : `middle-${si}`);
                const part = sec.door_parts?.[partKey];
                if (part?.enabled === false) return null;
                const maxHeightMm = Math.round(sh / scale * 10);
                const doorHeight = Math.min(sh, ((part?.height_mm || maxHeightMm) / 10) * scale);
                const anchoredBottom = partKey === "bottom";
                const doorY = anchoredBottom ? dy + sh - doorHeight : dy;
                const doorBottom = doorY + doorHeight;
                const standard = resolveDoorStandard({ ...sec, door_standard: partKey === "full" ? sec.door_standard : (part?.standard || sec.door_standard) }, dw / scale);
                const masterSide = part?.master === "right" ? "right" : sec.door_double_master === "right" ? "right" : "left";
                const panels = buildDoorPanels({ x: sx, width: dw, standard, masterSide }).sort((a, b) => (a.isMaster ? 1 : 0) - (b.isMaster ? 1 : 0));
                const handleY = anchoredBottom ? doorY : doorBottom;
                return (
                  <g key={`doorseg-${idx}-${si}`}>
                    {panels.map((panel, panelIndex) => {
                      // Portion visible du vantail sous le toit : si la pente mange tout le vantail, il n'existe pas.
                      const lowestTop = Math.max(leafTopAt(panel.x, doorY), leafTopAt(panel.x + panel.width, doorY));
                      const cueTop = Math.min(lowestTop, doorBottom - 1);
                      const cueH = doorBottom - cueTop;
                      if (Math.min(leafTopAt(panel.x, doorY), leafTopAt(panel.x + panel.width, doorY)) >= doorBottom - 6) return null;
                      return (
                        <g key={`doorleaf-${idx}-${si}-${panelIndex}`} pointerEvents="none" data-testid={`door-leaf-${idx}-${si}-${panelIndex}`} data-sloped={underSlope && lowestTop > doorY + 0.5 ? "true" : "false"}>
                          <path d={leafPath(panel.x, panel.width, doorY, doorBottom)} transform="translate(1 2)" fill="rgba(0,0,0,0.14)" />
                          <path d={leafPath(panel.x, panel.width, doorY, doorBottom)} fill={doorFillUsed} opacity={isCoulissant ? 0.4 : isMirrorDoor ? 0.72 : 0.36} />
                          {isMirrorDoor && <rect x={panel.x + panel.width * 0.12} y={cueTop + 2} width={panel.width * 0.18} height={Math.max(0, cueH - 4)} fill="rgba(255,255,255,0.55)" rx={2} />}
                          {!isMirrorDoor && <path d={leafPath(panel.x, panel.width, doorY, doorBottom)} fill={`url(#${grainId})`} opacity={0.12} />}
                          <path d={leafPath(panel.x, panel.width, doorY, doorBottom)} fill="none" stroke={doorDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.65)"} strokeWidth={2.5} strokeLinejoin="round" />
                          {panel.width > 14 && cueH > 14 && <path d={leafPath(panel.x, panel.width, doorY, doorBottom, 5)} fill="none" stroke={doorDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"} strokeWidth={1.2} strokeLinejoin="round" />}
                          {cueH > 20 && <DoorHingeCue
                            x={panel.x}
                            y={cueTop}
                            w={panel.width}
                            h={cueH}
                            direction={panel.direction}
                            dark={doorDark}
                            testId={`door-opening-marker-${idx}-${si}-${panelIndex}`}
                          />}
                          {!isCoulissant && panel.isMaster && cueH > 20 && handle?.svg_type !== "push" && <HandleGlyph type="bequille" x={panel.x} y={cueTop} w={panel.width} h={cueH} finishColor={handle?.swatch || "#9A9D9F"} direction={panel.direction} />}
                        </g>
                      );
                    })}
                    {onResizeDoorPart && dw > 24 && (
                      <g data-testid={`door-resize-handle-${idx}-${si}`} data-svg-element style={{ cursor: "ns-resize", touchAction: "none", pointerEvents: "all" }}
                        onPointerDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          event.currentTarget.setPointerCapture?.(event.pointerId);
                          const ctm = event.currentTarget.ownerSVGElement?.getScreenCTM();
                          const screenPxPerSvgUnit = ctm ? Math.hypot(ctm.c, ctm.d) : 1;
                          doorResizeRef.current = {
                            sectionIdx: idx,
                            partKey,
                            startY: event.clientY,
                            startMm: Math.round(doorHeight / scale * 10),
                            maxMm: maxHeightMm,
                            pxPerMm: Math.max(0.001, screenPxPerSvgUnit * scale / 10),
                            bottom: anchoredBottom,
                          };
                        }}>
                        <rect x={sx + dw / 2 - 14} y={handleY - 6} width={28} height={12} fill="transparent" />
                        <rect x={sx + dw / 2 - 11} y={handleY - 2.5} width={22} height={5} rx={2.5} fill="#2563EB" stroke="#FFF" strokeWidth={1} />
                        <line x1={sx + dw / 2 - 6} x2={sx + dw / 2 + 6} y1={handleY} y2={handleY} stroke="#FFF" strokeWidth={1} />
                      </g>
                    )}
                    {onRemoveDoorSegment && finalSegments.length > 1 && (
                      <SvgTrashButton
                        x={sx + dw - 7}
                        y={dy + 9}
                        onClick={() => onRemoveDoorSegment(idx, ((dy - topBoundary) / dh) * height * 10, (sh / dh) * height * 10)}
                        testId={`remove-door-segment-${idx}-${si}`}
                      />
                    )}
                  </g>
                );
              })}
              {/* Bouton "retirer toute la porte" : uniquement quand il n'y a qu'un seul segment.
                  Avec un tiroir qui scinde la porte en haut/bas, ce bouton global ne doit PAS
                  apparaître : il serait confondu avec la suppression du segment du haut et
                  supprimerait aussi le segment du bas (régression critique). Chaque segment
                  garde alors son propre bouton de suppression indépendant (remove-door-segment). */}
              {onRemoveDoor && finalSegments.length <= 1 && <SvgTrashButton x={sx + 8} y={topBoundary + 9} onClick={() => onRemoveDoor(idx)} testId={`remove-door-${idx}`} />}
              {onOpenDoorMenu && <g data-testid={`door-options-${idx}`} transform={`translate(${sx + 27}, ${topBoundary + 9})`} style={{ cursor: "pointer", pointerEvents: "all" }} onClick={(event) => { event.preventDefault(); event.stopPropagation(); onOpenDoorMenu(idx, event.clientX, event.clientY); }}><circle r="9" fill="#2563EB" stroke="#FFFFFF" strokeWidth="1.5" /><circle cx="-3.5" cy="0" r="1.4" fill="#FFF" /><circle cx="0" cy="0" r="1.4" fill="#FFF" /><circle cx="3.5" cy="0" r="1.4" fill="#FFF" /></g>}
            </g>
          );
        })}

        {activeMode === "door" && onPlaceDoor && sections.map((_section, idx) => {
          const x = originX + sectionOffsetsPx[idx];
          const sectionWidth = sectionWidthsPx[idx];
          const commitDoor = (event) => {
            event.preventDefault();
            event.stopPropagation();
            setHoverInfo(null);
            onPlaceDoor(idx);
          };
          return (
            <rect
              key={`door-placement-target-${idx}`}
              data-testid={`door-placement-target-${String.fromCharCode(65 + idx)}`}
              data-svg-element
              x={x}
              y={originY}
              width={sectionWidth}
              height={h}
              fill="transparent"
              pointerEvents="all"
              style={{ cursor: "copy", touchAction: "manipulation" }}
              onDragOver={(event) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = "copy";
                setHoverInfo({ idx, kind: "door" });
              }}
              onDragLeave={() => setHoverInfo(null)}
              onDrop={commitDoor}
              onClick={commitDoor}
            />
          );
        })}

        {/* Tiroirs traversants : une façade pleine largeur recouvre le montant entre deux travées.
            Chantier 3.1 : également redimensionnable en hauteur comme un tiroir classique. */}
        {traversingDrawers.map((drawer) => {
          const pxPerMm = scale / 10;
          const drH = Math.max(10, (drawer.height_mm || 150) * pxPerMm);
          const drY = drawer.y - drH / 2;
          const isHoverMe = hoveredEl?.elId === drawer.id;
          const isSelected = selectedEl?.elId === drawer.id;
          const isDraggingMe = dragging?.elId === drawer.id;
          const showResize = (isHoverMe || isSelected || isDraggingMe) && !!onResizeElement;
          const showActions = (isHoverMe || isSelected) && !drawer.locked;
          const interactiveProps = {
            "data-svg-element": true,
            "data-testid": `svg-traversing-drawer-${drawer.id}`,
            style: { cursor: "grab", touchAction: "none" },
            onPointerDown: (event) => {
              event.preventDefault();
              event.stopPropagation();
              setSelectedEl({ sectionIdx: drawer.sectionIdx, elId: drawer.id });
              onElementSelect?.({ sectionIdx: drawer.sectionIdx, elId: drawer.id });
              setDragging({ sectionIdx: drawer.sectionIdx, elId: drawer.id });
            },
            onMouseEnter: () => setHoveredEl({ sectionIdx: drawer.sectionIdx, elId: drawer.id }),
            onMouseLeave: () => setHoveredEl(null),
          };
          const startResize = (edge) => (event) => {
            event.preventDefault();
            event.stopPropagation();
            event.currentTarget.setPointerCapture?.(event.pointerId);
            const ctm = event.currentTarget.ownerSVGElement?.getScreenCTM();
            const screenPxPerSvgUnit = ctm ? Math.hypot(ctm.c, ctm.d) : 1;
            const half = (drawer.height_mm || 150) / 2;
            elResizeRef.current = {
              sectionIdx: drawer.sectionIdx,
              elId: drawer.id,
              edge,
              startY: event.clientY,
              startEdgeYMm: edge === "bottom" ? drawer.y_mm - half : drawer.y_mm + half,
              pxPerMm: Math.max(0.001, screenPxPerSvgUnit * pxPerMm),
            };
          };
          const removeHere = () => {
            onRemoveElement && onRemoveElement(drawer.sectionIdx, drawer.id);
            setSelectedEl(null);
            setHoveredEl(null);
          };
          return (
            <g key={`traversing-${drawer.id}`} {...interactiveProps} clipPath={underSlope ? `url(#${slopeClipId})` : undefined}>
              <rect x={drawer.x + 4} y={drY + 2} width={drawer.width - 8} height={drH - 3} fill="rgba(0,0,0,0.22)" rx={2} />
              <rect x={drawer.x + 3} y={drY + 1} width={drawer.width - 6} height={drH - 2} fill={doorFill} opacity={0.98} stroke={drawerBorder} strokeWidth={isDraggingMe ? 4 : 2.5} rx={2} />
              <rect x={drawer.x + drawer.width / 2 - 12} y={drY + drH / 2 - 1.5} width={24} height={3} fill={isDarkDoor ? "#F5F5F5" : "#0F0F0F"} rx={1.2} />
              {(isHoverMe || isSelected || isDraggingMe) && (
                <>
                  <rect x={drawer.x + drawer.width / 2 - 40} y={drY - 14} width={80} height={16} rx={8} fill="#111" />
                  <text x={drawer.x + drawer.width / 2} y={drY - 2} textAnchor="middle" fontSize="10" fontWeight="700" fill="#FFF" fontFamily="Outfit">H : {Math.round(drawer.height_mm || 150)} mm</text>
                </>
              )}
              {showResize && (
                <g data-testid={`drawer-resize-top-${drawer.id}`} data-svg-element style={{ cursor: "ns-resize", touchAction: "none", pointerEvents: "all" }} onPointerDown={startResize("top")}>
                  <rect x={drawer.x + drawer.width / 2 - 14} y={drY - 5} width={28} height={10} fill="transparent" />
                  <rect x={drawer.x + drawer.width / 2 - 11} y={drY - 3} width={22} height={4} rx={2} fill="#2563EB" stroke="#FFF" strokeWidth={0.8} />
                </g>
              )}
              {showResize && (
                <g data-testid={`drawer-resize-bottom-${drawer.id}`} data-svg-element style={{ cursor: "ns-resize", touchAction: "none", pointerEvents: "all" }} onPointerDown={startResize("bottom")}>
                  <rect x={drawer.x + drawer.width / 2 - 14} y={drY + drH - 5} width={28} height={10} fill="transparent" />
                  <rect x={drawer.x + drawer.width / 2 - 11} y={drY + drH - 3} width={22} height={4} rx={2} fill="#2563EB" stroke="#FFF" strokeWidth={0.8} />
                </g>
              )}
              <text x={drawer.x + drawer.width / 2} y={drY + drH + 30} textAnchor="middle" fontSize="8" fontWeight="700" fill={drawerBorder} fontFamily="Outfit">TIROIR TRAVERSANT</text>
              {/* Pas de duplication pour un tiroir traversant (règle atelier : d'abord le ramener à une section). */}
              <SvgTrashButton x={drawer.x + drawer.width / 2} y={drY + drH + 16} onClick={removeHere} visible={showActions} testId={`trash-${drawer.id}`} />
            </g>
          );
        })}

        {/* Poignée visuelle : un clic ajoute exactement une travée à droite, sans automatisme. */}
        {onExtendDrawer && sections.flatMap((section, sectionIdx) => (section.elements || [])
          .filter((element) => element.kind === "drawer" && sectionIdx + (element.span_sections || 1) < sectionCount)
          .map((element) => ({
            element,
            sectionIdx,
            x: originX + sectionOffsetsPx[sectionIdx] + sectionWidthsPx.slice(sectionIdx, sectionIdx + (element.span_sections || 1)).reduce((sum, sectionWidth) => sum + sectionWidth, 0),
            y: originY + 4 + (h - 8) * (1 - element.y_mm / (height * 10)),
          })))
          .map(({ element, sectionIdx, x, y }) => (
            <g
              key={`extend-${element.id}`}
              data-testid={`extend-drawer-${element.id}`}
              transform={`translate(${x - 10}, ${y - 9})`}
              style={{ cursor: "pointer" }}
              onPointerDown={(event) => { event.preventDefault(); event.stopPropagation(); }}
              onClick={(event) => { event.preventDefault(); event.stopPropagation(); onExtendDrawer(sectionIdx, element.id); }}
            >
              <circle cx="0" cy="0" r="9" fill="#0465C1" stroke="#FFFFFF" strokeWidth="1.5" />
              <path d="M-3 -3 L3 0 L-3 3 M3 0 L-5 0" fill="none" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </g>
          ))}

        {/* Flèche inverse : réduit une travée et permet de revenir à un tiroir d'une seule colonne. */}
        {onReduceDrawer && sections.flatMap((section, sectionIdx) => (section.elements || [])
          .filter((element) => element.kind === "drawer" && (element.span_sections || 1) > 1)
          .map((element) => ({
            element,
            sectionIdx,
            x: originX + sectionOffsetsPx[sectionIdx],
            y: originY + 4 + (h - 8) * (1 - element.y_mm / (height * 10)),
          })))
          .map(({ element, sectionIdx, x, y }) => (
            <g
              key={`reduce-${element.id}`}
              data-testid={`reduce-drawer-${element.id}`}
              transform={`translate(${x + 10}, ${y - 9})`}
              style={{ cursor: "pointer" }}
              onPointerDown={(event) => { event.preventDefault(); event.stopPropagation(); }}
              onClick={(event) => { event.preventDefault(); event.stopPropagation(); onReduceDrawer(sectionIdx, element.id); }}
            >
              <circle cx="0" cy="0" r="9" fill="#405166" stroke="#FFFFFF" strokeWidth="1.5" />
              <path d="M3 -3 L-3 0 L3 3 M-3 0 L5 0" fill="none" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </g>
          ))}

        {/* Options pose Lot 15 : Fileurs (habillage latéral/haut) — largeur DYNAMIQUE en cm réel */}
        {options && (
          <g pointerEvents="none">
            {options.fileur_left && (() => {
              const fw = Math.max(4, (options.fileur_left_width || 1.5) * cmToSvg);
              const topInner = slopeTopAtX(originX), topOuter = slopeTopAtX(originX - fw);
              return (
                <>
                  {underSlope ? <path d={`M ${originX - fw} ${topOuter} L ${originX} ${topInner} L ${originX} ${originY + h} L ${originX - fw} ${originY + h} Z`} fill={carcassColor} stroke={strokeCol} strokeWidth={1} /> : <rect x={originX - fw} y={originY} width={fw} height={h} fill={carcassColor} stroke={strokeCol} strokeWidth={1} />}
                  <text x={originX - fw / 2} y={originY + h / 2} textAnchor="middle" fontSize="7" fill={strokeCol} fontFamily="Outfit" transform={`rotate(-90 ${originX - fw / 2} ${originY + h / 2})`}>Fileur G {Math.round((options.fileur_left_width || 1.5) * 10)}mm</text>
                </>
              );
            })()}
            {options.fileur_right && (() => {
              const fw = Math.max(4, (options.fileur_right_width || 1.5) * cmToSvg);
              const topInner = slopeTopAtX(originX + w), topOuter = slopeTopAtX(originX + w + fw);
              return (
                <>
                  {underSlope ? <path d={`M ${originX + w} ${topInner} L ${originX + w + fw} ${topOuter} L ${originX + w + fw} ${originY + h} L ${originX + w} ${originY + h} Z`} fill={carcassColor} stroke={strokeCol} strokeWidth={1} /> : <rect x={originX + w} y={originY} width={fw} height={h} fill={carcassColor} stroke={strokeCol} strokeWidth={1} />}
                  <text x={originX + w + fw / 2} y={originY + h / 2} textAnchor="middle" fontSize="7" fill={strokeCol} fontFamily="Outfit" transform={`rotate(90 ${originX + w + fw / 2} ${originY + h / 2})`}>Fileur D {Math.round((options.fileur_right_width || 1.5) * 10)}mm</text>
                </>
              );
            })()}
            {options.fileur_top && (() => {
              const fh = Math.max(4, (options.fileur_top_width || 1.5) * cmToSvg);
              const roofPoints = Array.from({ length: 25 }, (_, index) => {
                const x = originX + (w * index) / 24;
                return [x, slopeTopAtX(x)];
              });
              const profiledFileur = `M ${roofPoints.map(([x, y]) => `${x} ${y}`).join(" L ")} L ${roofPoints.slice().reverse().map(([x, y]) => `${x} ${y - fh}`).join(" L ")} Z`;
              return (
                <>
                  {underSlope ? <path d={profiledFileur} fill={carcassColor} stroke={strokeCol} strokeWidth={1} /> : <rect x={originX} y={originY - fh} width={w} height={fh} fill={carcassColor} stroke={strokeCol} strokeWidth={1} />}
                  <text x={originX + w / 2} y={originY - fh / 2 + 2} textAnchor="middle" fontSize="7" fill={strokeCol} fontFamily="Outfit">Fileur haut {Math.round((options.fileur_top_width || 1.5) * 10)}mm</text>
                </>
              );
            })()}
          </g>
        )}

        {/* Pieds / Plinthe */}
        {(templateVariant === "sdb" || templateVariant === "sdb_column") && options?.sdb_foot_ellipse_height && options?.feet_visible !== false && (() => {
          const footCm = options.sdb_foot_ellipse_height / 10;
          const footPx = footCm * scale;
          const yFront = originY + h;
          
          // 4 pieds en perspective 2D
          // Pieds arrières (plus petits, plus haut, plus foncés)
          const backPositions = [{ x: originX + 16 * scale }, { x: originX + w - 21 * scale }];
          // Pieds avants
          const frontPositions = [{ x: originX + 8 * scale }, { x: originX + w - 13 * scale }];
          
          return (
            <>
              {backPositions.map((p, i) => (
                <g key={`sdb-foot-back-${i}`} opacity="0.6">
                   <ellipse cx={p.x + 2 * scale} cy={yFront + (footPx * 0.7) / 2 - 2} rx={2 * scale} ry={(footPx * 0.7) / 2} fill="#111" />
                   <rect x={p.x} y={yFront - 2} width={4 * scale} height={footPx * 0.7} fill="#A1A5AB" stroke="#6E7277" strokeWidth={0.5} rx={1} />
                </g>
              ))}
              {frontPositions.map((p, i) => (
                <g key={`sdb-foot-front-${i}`}>
                   <ellipse cx={p.x + 2.5 * scale} cy={yFront + footPx / 2} rx={2.5 * scale} ry={footPx / 2} fill="#222" />
                   <rect x={p.x} y={yFront} width={5 * scale} height={footPx} fill="#D1D5DB" stroke="#8E9297" strokeWidth={0.5} rx={1} />
                </g>
              ))}
            </>
          );
        })()}
        {(() => {
          const yFront = originY + h;
          const isSdb = templateVariant === "sdb" || templateVariant === "sdb_column";
          // Règle impérative : jamais de plinthe en Salle de Bain, quelle que soit la valeur
          // stockée dans options.plinth (anciens devis/brouillons sauvegardés avant ce correctif inclus).
          const hasPlinth = isSdb ? false : options?.plinth !== false;
          
          if (hasPlinth) {
            // Plinthe intégrée s'adapte à la hauteur des pieds choisis (si SdB)
            const sdbFootCm = isSdb && options?.sdb_foot_ellipse_height ? options.sdb_foot_ellipse_height / 10 : 0;
            const plinthCm = sdbFootCm > 0 ? sdbFootCm : 10;
            const plinthPx = plinthCm * scale;

            return (
              <g pointerEvents="none">
                <rect x={originX} y={yFront} width={w} height={plinthPx} fill={carcassColor} stroke={strokeCol} strokeWidth={1} />
                <rect x={originX} y={yFront} width={w} height={plinthPx} fill={`url(#${grainId})`} opacity={0.2} />
                <text x={originX + w / 2} y={yFront + plinthPx - 2} textAnchor="middle" fontSize="7" fill={strokeCol} fontFamily="Outfit">Plinthe</text>
              </g>
            );
          }
          
          if (templateVariant === "sdb" || templateVariant === "sdb_column" || options?.feet_visible === false) return null;

          // Pieds visibles (Dressing/Cuisine)
          const positions = [{ x: originX + 4 }, { x: originX + w - 10 }];
          for (let i = 0; i < sectionCount - 1; i++) {
            const xSep = originX + sectionOffsetsPx[i + 1] - 3;
            positions.push({ x: xSep });
          }
          return positions.map((p, i) => (
            <rect key={`foot-${i}`} x={p.x} y={yFront} width={6} height={8} fill={carcassColor} stroke={strokeCol} strokeWidth={0.8} />
          ));
        })()}

        {/* Section labels A, B, C, D + largeur. Les labels sont empilés verticalement
            (pastille lettre sur une ligne, largeur cm sur la ligne d'en-dessous) pour éviter
            tout chevauchement horizontal, même sur des sections étroites. Si la section fait
            moins de 26 px de large, seule la pastille est affichée. */}
        {sections.map((sec, idx) => {
          const cx = originX + sectionOffsetsPx[idx] + sectionWidthsPx[idx] / 2;
          const showDim = sectionWidthsPx[idx] >= 26;
          const wide = Math.max((sec.width_cm || 0) * 10, (sectionWidthsPx[idx] / scale) * 10) > 600;
          const activeStandard = resolveDoorStandard(sec, sectionWidthsPx[idx] / scale);
          const facadeValue = sec.door_mode === "none" || !sec.has_door ? "none" : (activeStandard === "single_left" ? "single-left" : activeStandard === "double" ? "double" : "single-right");
          return (
            <g key={`lbl-${idx}`} data-testid={`section-control-${idx}`} transform={`translate(${cx}, ${originY + h + 30})`} style={{ cursor: "pointer" }} onClick={() => onSectionSelect?.(idx)}>
              <circle cx={0} cy={0} r={10} fill="hsl(210 96% 39%)" />
              <text x={0} y={4} textAnchor="middle" fontSize="11" fill="#FFF" fontFamily="Outfit" fontWeight="700">
                {String.fromCharCode(65 + idx)}
              </text>
              {showDim && (
                editingSectionWidthIdx === idx ? (
                  <foreignObject x={-48} y={10} width={96} height={26} style={{ overflow: "visible" }}>
                    <input
                      type="number"
                      autoFocus
                      data-testid={`section-width-input-${idx}`}
                      defaultValue={Math.round(widthsCm[idx] * 10)}
                      onClick={(event) => event.stopPropagation()}
                      onFocus={(event) => event.target.select()}
                      onBlur={(event) => commitSectionWidthEdit(idx, event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") { event.currentTarget.blur(); }
                        if (event.key === "Escape") { setEditingSectionWidthIdx(null); }
                      }}
                      style={{
                        width: "94px", height: "24px", fontSize: "13px", fontWeight: 700, textAlign: "center", boxSizing: "border-box",
                        border: sectionWidthError?.idx === idx ? "2px solid #DC2626" : "2px solid #0465C1",
                        borderRadius: "6px", background: sectionWidthError?.idx === idx ? "#FEE2E2" : "#FFFFFF", color: "#0F172A",
                        padding: 0, outline: "none", boxShadow: "0 2px 8px rgba(15,23,42,0.35)",
                      }}
                    />
                  </foreignObject>
                ) : (
                  <g
                    style={{ cursor: onEditSectionWidth ? "pointer" : "default" }}
                    onClick={(event) => { if (!onEditSectionWidth) return; event.stopPropagation(); setEditingSectionWidthIdx(idx); }}
                  >
                    {/* Pastille de fond pleine (contraste garanti quelle que soit la page hôte, ex. iFrame à fond sombre) + zone de clic agrandie */}
                    <rect
                      data-testid={`section-width-label-${idx}-pill`}
                      x={sectionWidthError?.idx === idx ? -54 : -48} y={10} width={sectionWidthError?.idx === idx ? 108 : 96} height={21} rx={10.5}
                      fill={sectionWidthError?.idx === idx ? "#FEE2E2" : "#FFFFFF"}
                      stroke={sectionWidthError?.idx === idx ? "#DC2626" : "#0465C1"}
                      strokeWidth={sectionWidthError?.idx === idx ? 1.6 : 1.3}
                    />
                    <text
                      data-testid={`section-width-label-${idx}`}
                      x={onEditSectionWidth && !(sectionWidthError?.idx === idx) ? -12 : 0} y={25} textAnchor="middle" fontSize={sectionWidthError?.idx === idx ? "9.5" : "11"} fontFamily="Outfit"
                      fontWeight="700"
                      fill={sectionWidthError?.idx === idx ? "#DC2626" : "#0465C1"}
                    >
                      {sectionWidthError?.idx === idx ? sectionWidthError.message : `${Math.round(widthsCm[idx] * 10)} mm`}
                    </text>
                    {/* Icône crayon : affordance explicite "modifiable au clavier" — décalée pour ne jamais chevaucher le texte (même pour 2700 mm) */}
                    {onEditSectionWidth && !(sectionWidthError?.idx === idx) && (
                      <g transform="translate(38, 20.5)" stroke="#0465C1" strokeWidth={1.4} fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M-4.2 4.2 L-5 6.5 L-2.7 5.8 L5.3 -2.2 C5.7 -2.6 5.7 -3.3 5.3 -3.7 L4.1 -4.9 C3.7 -5.3 3 -5.3 2.6 -4.9 L-5.4 3.1" />
                        <path d="M1.6 -3.9 L3.9 -1.6" />
                      </g>
                    )}
                  </g>
                )
              )}
              {onRemoveSection && sections.length > 1 && <SvgTrashButton x={14} y={-9} onClick={() => onRemoveSection(idx)} testId={`remove-section-visual-${idx}`} />}
              {onFacadeMode && sectionWidthsPx[idx] >= 42 && <foreignObject x={-sectionWidthsPx[idx] / 2} y={34} width={sectionWidthsPx[idx]} height={30} style={{ overflow: "hidden" }}>
                <select data-testid={`schema-facade-mode-${idx}`} value={facadeValue} onClick={(event) => event.stopPropagation()} onChange={(event) => { const value = event.target.value; if (value === "none") onFacadeMode(idx, "none"); else onFacadeMode(idx, value === "double" ? "double" : "single", value === "single-left" ? "single_left" : value === "double" ? "double" : "single_right"); }} style={{ width: "100%", minWidth: 0, height: "24px", fontSize: sectionWidthsPx[idx] < 70 ? "6px" : "8px", border: "1px solid #B8C2CF", borderRadius: "4px", background: "#FFF", color: "#111", padding: "0 1px" }}>
                  <option value="none">Aucune porte</option>
                  {wide
                    ? <option value="double">2 portes battantes</option>
                    : <>
                        <option value="single-left">1 porte gauche</option>
                        <option value="single-right">1 porte droite</option>
                        <option value="double">2 portes battantes</option>
                      </>}
                </select>
              </foreignObject>}
            </g>
          );
        })}

        {/* Cote de largeur globale — affichée UNIQUEMENT quand il y a plusieurs sections
            (pour 1 section, le label section indique déjà la largeur ; l'afficher 2×
            provoquait un dédoublement visuel "100 100"). */}
        {sectionCount > 1 && (
          <text x={originX + w / 2} y={originY + h + 100} textAnchor="middle" fontSize="11" fill={mutedCol} fontFamily="Outfit">
            Largeur totale : {totalWidth * 10} mm
          </text>
        )}
        {/* Cote de hauteur — toujours plaquée au bord gauche (x=14), DEVANT un éventuel fileur gauche
            (qui grandit entre 30 et originX) : jamais de chevauchement quelle que soit sa largeur. */}
        <text x={14} y={originY + h / 2} textAnchor="middle" fontSize="11" fontWeight="700" fill={mutedCol} fontFamily="Outfit" transform={`rotate(-90 14 ${originY + h / 2})`}>
          {height * 10} mm sans pieds
        </text>
        {/* Note "si pieds" — placée à GAUCHE du schéma (verticale, comme la cote de hauteur),
            juste avant elle, pour ne jamais se mélanger avec le plan/la crédence SdB en haut. */}
        <text data-testid="height-feet-note" x={4} y={originY + h / 2} textAnchor="middle" fontSize="7.5" fontWeight="700" fill={mutedCol} fontFamily="Outfit" transform={`rotate(-90 4 ${originY + h / 2})`}>
          Hauteur si pieds : +100mm
        </text>

        {/* Poignées de redimensionnement global — agrandir/rétrécir le meuble entier directement
            sur le schéma (bord droit = largeur, bord bas = hauteur), même principe que les
            séparations de section. Non proposé pour les gammes SdB (dimensions contraintes). */}
        {onResizeTotal && templateVariant !== "sdb" && templateVariant !== "sdb_column" && (
          <>
            {/* Largeur — bord droit */}
            <rect
              data-testid="resize-handle-width"
              data-svg-element
              aria-label="Agrandir ou réduire la largeur totale"
              x={originX + w - 6}
              y={originY + h / 2 - 20}
              width={12}
              height={40}
              fill="transparent"
              style={{ cursor: "ew-resize", touchAction: "none" }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              e.currentTarget.setPointerCapture?.(e.pointerId);
              const svg = e.currentTarget.ownerSVGElement;
              const ctm = svg?.getScreenCTM();
              const screenPxPerSvgUnit = ctm ? Math.hypot(ctm.a, ctm.b) : 1;
              resizeDragRef.current = {
                axis: "width",
                startClient: e.clientX,
                startValueCm: totalWidth,
                pxPerCm: Math.max(0.01, screenPxPerSvgUnit * cmToSvg),
              };
                setResizeDragging({ axis: "width" });
              }}
            />
            <rect
              x={originX + w - 3}
              y={originY + h / 2 - 16}
              width={6}
              height={32}
              rx={3}
              fill={resizeDragging?.axis === "width" ? "#D97706" : "rgba(217,119,6,0.4)"}
              pointerEvents="none"
            />
            <line x1={originX + w - 1} x2={originX + w - 1} y1={originY + h / 2 - 6} y2={originY + h / 2 + 6} stroke="#FFF" strokeWidth={0.8} pointerEvents="none" />
            <line x1={originX + w + 1} x2={originX + w + 1} y1={originY + h / 2 - 6} y2={originY + h / 2 + 6} stroke="#FFF" strokeWidth={0.8} pointerEvents="none" />
            {resizeDragging?.axis === "width" && (
              <>
                <rect x={originX + w - 38} y={originY - 22} width={76} height={16} rx={8} fill="#D97706" />
                <text x={originX + w} y={originY - 10} textAnchor="middle" fontSize="10" fontWeight="700" fill="#FFF" fontFamily="Outfit">
                  {Math.round(totalWidth * 10)} mm
                </text>
              </>
            )}

            {/* Hauteur — bord bas */}
            <rect
              data-testid="resize-handle-height"
              data-svg-element
              aria-label="Agrandir ou réduire la hauteur totale"
              x={originX + w / 2 - 20}
              y={originY + h - 6}
              width={40}
              height={12}
              fill="transparent"
              style={{ cursor: "ns-resize", touchAction: "none" }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              e.currentTarget.setPointerCapture?.(e.pointerId);
              const svg = e.currentTarget.ownerSVGElement;
              const ctm = svg?.getScreenCTM();
              const screenPxPerSvgUnit = ctm ? Math.hypot(ctm.c, ctm.d) : 1;
              resizeDragRef.current = {
                axis: "height",
                startClient: e.clientY,
                startValueCm: height,
                pxPerCm: Math.max(0.01, screenPxPerSvgUnit * scale),
              };
                setResizeDragging({ axis: "height" });
              }}
            />
            <rect
              x={originX + w / 2 - 16}
              y={originY + h - 3}
              width={32}
              height={6}
              rx={3}
              fill={resizeDragging?.axis === "height" ? "#D97706" : "rgba(217,119,6,0.4)"}
              pointerEvents="none"
            />
            <line x1={originX + w / 2 - 6} x2={originX + w / 2 + 6} y1={originY + h - 1} y2={originY + h - 1} stroke="#FFF" strokeWidth={0.8} pointerEvents="none" />
            <line x1={originX + w / 2 - 6} x2={originX + w / 2 + 6} y1={originY + h + 1} y2={originY + h + 1} stroke="#FFF" strokeWidth={0.8} pointerEvents="none" />
            {resizeDragging?.axis === "height" && (
              <>
                <rect x={originX + w + 8} y={originY + h / 2 - 8} width={92} height={16} rx={8} fill="#D97706" />
                <text x={originX + w + 54} y={originY + h / 2 + 4} textAnchor="middle" fontSize="10" fontWeight="700" fill="#FFF" fontFamily="Outfit">
                  {Math.round(height * 10)} mm sans pieds
                </text>
              </>
            )}
          </>
        )}
      </motion.svg>
    </div>
  );
}
